import { useTheme } from 'styled-components';
import {
  BusinessOutlineIc,
  CheckmarkDoneIc,
  ShieldCheckmark2Ic,
  ShieldOutlineIc,
  StarOutlineIc,
} from '@dsch/dd-icons';
import { Button } from '../../Toolkit/Button';
import { Modal } from '../../ToolkitV2/Modal/Modal';
import { ModalHeader } from '../../ToolkitV2/Modal/subcomponents/ModalHeader';
import { Text } from '../../ToolkitV2/Text/Text';
import * as Styled from './TrustedDealerModalV2.styled';
import type { TrustedDealerModalProps } from './TrustedDealerModal.typed';

/**
 * TrustedDealerModalV2 - Updated version of the Trusted Dealer Programme modal
 * with revised criteria and content.
 *
 * @remarks
 * This modal is conditionally rendered based on the FEATURE_TDP_UPDATES flag.
 * Displays updated TDP criteria including warranties, reviews (4.0+), history checks, and business age.
 * The original modal (TrustedDealerModal) should be removed in the cleanup of the feature flag.
 *
 * @param props.isOpen - Controls modal visibility
 * @param props.askToClose - Callback function when modal close is requested
 */
function TrustedDealerModalV2(props: Readonly<TrustedDealerModalProps>) {
  const { isOpen, askToClose } = props;
  const theme = useTheme();

  const commonIconProps = {
    width: 20,
    height: 20,
    color: theme.donedeal.colors.GREEN,
  };

  const criteriaList = [
    {
      title: 'Warranties Offered',
      body: 'Protection for added peace of mind',
      icon: <ShieldOutlineIc {...commonIconProps} />,
    },
    {
      title: 'Outstanding Reviews',
      body: 'Google rating of 4.0 stars or higher',
      icon: <StarOutlineIc {...commonIconProps} />,
    },
    {
      title: 'History Checked Vehicles',
      body: 'Vehicle History and ownership verified',
      icon: <CheckmarkDoneIc {...commonIconProps} />,
    },
    {
      title: 'Established Business',
      body: 'Advertising on DoneDeal for over 12 months',
      icon: <BusinessOutlineIc {...commonIconProps} />,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={askToClose}
      contentLabel="Trusted Dealer Programme Information"
      modalClassName="tdp-modal"
      width="320px"
      borderRadius={theme.donedeal.borderRadius.large}
      padding={theme.donedeal.spacing.M24}
      data-testid="tdp-modal"
      // TDP modal should appear above filters modal (Mobile) as it is accessible from there
      zIndex={theme.donedeal.zIndices.MODAL + 1}
    >
      <ModalHeader
        modalName="trusted-dealer-modal"
        onRequestClose={askToClose}
        text={
          <Styled.Header>
            <ShieldCheckmark2Ic
              width={24}
              height={24}
              color={theme.donedeal.colors.GREEN}
            />
            <Text size="L18" weight="semibold" forwardedAs="h1">
              Trusted Dealer
            </Text>
          </Styled.Header>
        }
        forwardedAs="div"
      />
      <Styled.OpeningText>
        Dealers in this programme meet DoneDeal's highest standards for trust,
        service and transparency so you can buy with peace of mind.
      </Styled.OpeningText>
      <Styled.Divider />
      <Styled.BodyText>
        {criteriaList.map((criteria) => (
          <Styled.SInfoListItem key={criteria.title} {...criteria} />
        ))}
      </Styled.BodyText>
      <Styled.Divider />
      <Styled.ClosingText>
        These dealers go above and beyond giving you stronger signals of trust
        and support, every step of the way.
      </Styled.ClosingText>
      <Styled.ButtonContainer>
        <Button
          onClick={askToClose}
          data-tracking="tdp-close-btn"
          data-testid="tdp-close-btn"
        >
          Okay, got it!
        </Button>
      </Styled.ButtonContainer>
    </Modal>
  );
}

export { TrustedDealerModalV2 };
